// Consolidated mock data for dispute detail panels
// Follows the pattern of mockPaymentsPanels.ts for dispute-specific detail views

import type { PaymentDetail, Receipt } from "@shared/components/panels/types/panel-common.types";
import type { EventData } from '@shared/types/events.types';
import type { BaseTimelineEvent } from '@shared/components/panels/components/domain-sections/Timeline/types';
import type { ActivityEvent } from '@shared/components/panels/components/domain-sections/Activity/types';

// ========================================
// Dispute Details
// ========================================

export const DISPUTE_DETAILS: PaymentDetail[] = [
  { label: 'Dispute Reference', value: 'DSP9k04m3p5s1b6' },
  { label: 'Original Payment', value: 'PAY-2024-09-28-001', isCopyable: true },
  { label: 'Trx Reference', value: 'TRX-2024-09-28-001' },
  { label: 'Dispute Date', value: '19 Jun 2025 10:30' },
  { label: 'Card Network Reference', value: 'MC 817289471294124' },
  { label: 'ARN', value: '68764873264527365423' },
  { label: 'Case Number', value: 'CB-2025-00847' },
  { label: 'Reason Code', value: '10.4 (Fraud - Card-Absent Environment)' },
  { label: 'Dispute Category', value: 'Fraud' },
  { label: 'Dispute Type', value: 'First Chargeback' },
  { label: 'Network', value: 'Mastercard' },
  { label: 'Acquirer', value: 'Chase Bank' },
  { label: 'Organization', value: 'BrikLabs' },
  { label: 'Product Platform', value: 'Core Commerce' },
  { label: 'Processor', value: 'Stripe' },
  { label: 'Merchant', value: 'BRIK US' },
];

export const DISPUTE_CUSTOMER_DETAILS: PaymentDetail[] = [
  { label: 'Account', value: 'user_9847abx52q' },
  { label: 'Name', value: 'John Doe' },
  { label: 'Email', value: 'johndoe@example.com' },
  { label: 'Billing address', value: '123 Main St, New York, NY 10001, United States' },
  { label: 'Risk', value: '7/50 (Medium Risk)' },
  { label: 'Previous disputes', value: '0' },
  { label: 'Last activity', value: '19 Jun 2025 10:30' }
];

export const DISPUTE_FEES_DETAILS: PaymentDetail[] = [
  { label: 'Disputed amount', value: 'BGN 80.00' },
  { label: 'Original payment', value: 'BGN 80.00' },
  { label: 'Dispute fee', value: 'USD 15.00' },
  { label: 'Held amount', value: 'BGN 80.00' },
  { label: 'Net impact (if lost)', value: 'BGN -95.00' },
  { label: 'Chargeback fee', value: 'USD 15.00' },
  { label: 'Representment fee (if challenged)', value: 'USD 0.00' },
];

export const DISPUTE_EVIDENCE_DETAILS: PaymentDetail[] = [
  { label: 'Evidence due by', value: '26 Jun 2025 23:59 UTC' },
  { label: 'Days remaining', value: '7 days' },
  { label: 'Evidence status', value: 'Not yet submitted',
    showBadge: true,
    badgeVariant: 'destructive'
  },
  { label: 'Recommended evidence', value: 'Shipping proof, delivery confirmation, customer communication' },
  { label: 'Win probability', value: '65% (based on reason code and evidence type)' },
];

export const DISPUTE_PAYMENT_METHOD_DETAILS: PaymentDetail[] = [
  { label: 'Payment method', value: 'Card' },
  { label: 'Card number', value: '•••• •••• •••• 0036' },
  { label: 'Card type', value: 'Mastercard' },
  { label: 'Expiry date', value: '12/2026' },
  { label: 'Cardholder name', value: 'John Doe' },
  { label: 'Billing address', value: '123 Main St, New York, NY 10001' },
  { label: 'Issuing bank', value: 'UniCredit Bulbank' },
  { label: 'Card country', value: 'Bulgaria' },
  { label: '3DS Authentication', value: 'Successful' },
  { label: 'Liability shift', value: 'Yes (Issuer)' }
];

// ========================================
// Timeline Events
// ========================================

export function generateDisputeTimelineEvents(): BaseTimelineEvent[] {
  return [
    {
      id: 'dispute-filed',
      title: 'Dispute filed by cardholder',
      timestamp: '19 Jun 2025 10:30',
      details: [
        { action: 'Cardholder contacted issuing bank', timestamp: '19 Jun 2025 10:30' },
        { action: 'Reason: Fraudulent transaction', timestamp: '19 Jun 2025 10:30' },
        { action: 'Disputed amount: BGN 80.00', timestamp: '19 Jun 2025 10:30' }
      ]
    },
    {
      id: 'dispute-received',
      title: 'Dispute notification received',
      timestamp: '19 Jun 2025 10:35',
      details: [
        { action: 'Chargeback received from Mastercard network', timestamp: '19 Jun 2025 10:35' },
        { action: 'Case number: CB-2025-00847', timestamp: '19 Jun 2025 10:35' },
        { action: 'Reason code: 10.4 (Fraud)', timestamp: '19 Jun 2025 10:35' }
      ]
    },
    {
      id: 'funds-held',
      title: 'Funds held from merchant balance',
      timestamp: '19 Jun 2025 10:36',
      details: [
        { action: 'BGN 80.00 withheld from next settlement', timestamp: '19 Jun 2025 10:36' },
        { action: 'Dispute fee of USD 15.00 charged', timestamp: '19 Jun 2025 10:36' }
      ]
    },
    {
      id: 'evidence-requested',
      title: 'Evidence submission requested',
      timestamp: '19 Jun 2025 10:36',
      details: [
        { action: 'Evidence deadline: 26 Jun 2025 23:59 UTC', timestamp: '19 Jun 2025 10:36' },
        { action: 'Recommended: shipping proof, delivery confirmation', timestamp: '19 Jun 2025 10:36' },
        { action: 'Notification sent to merchant', timestamp: '19 Jun 2025 10:37' }
      ]
    }
  ];
}

// ========================================
// Events Data
// ========================================

export const DISPUTE_EVENTS_DATA: EventData[] = [
  {
    id: 'evt_disp_001',
    eventType: 'dispute.created',
    description: 'Dispute dp_1234567890 received for BGN 80.00 on payment 8a6e7a2d. Reason: Fraudulent',
    timestamp: '19 Jun 2025 10:30',
    requestId: 'req_disp_abc123def456',
    status: 'pending'
  },
  {
    id: 'evt_disp_002',
    eventType: 'dispute.funds_held',
    description: 'BGN 80.00 held from merchant balance pending dispute resolution',
    timestamp: '19 Jun 2025 10:36',
    requestId: 'req_disp_def456ghi789',
    status: 'processing'
  },
  {
    id: 'evt_disp_003',
    eventType: 'dispute.evidence_requested',
    description: 'Evidence submission required by 26 Jun 2025 23:59 UTC to challenge dispute',
    timestamp: '19 Jun 2025 10:36',
    requestId: 'req_disp_ghi789jkl012',
    status: 'pending'
  },
  {
    id: 'evt_disp_004',
    eventType: 'webhook.sent',
    description: 'Webhook notification delivered for dispute.created event to merchant endpoint',
    timestamp: '19 Jun 2025 10:37',
    requestId: 'req_disp_jkl012mno345',
    status: 'succeeded'
  },
  {
    id: 'evt_disp_005',
    eventType: 'dispute.evidence_submitted',
    description: 'Merchant submitted evidence: shipping receipt, delivery confirmation, customer email',
    timestamp: '22 Jun 2025 14:15',
    requestId: 'req_disp_mno345pqr678',
    status: 'succeeded'
  },
  {
    id: 'evt_disp_006',
    eventType: 'dispute.under_review',
    description: 'Dispute under review by issuing bank. Expected resolution: 30-45 days',
    timestamp: '22 Jun 2025 14:20',
    requestId: 'req_disp_pqr678stu901',
    status: 'processing'
  },
  {
    id: 'evt_disp_007',
    eventType: 'dispute.won',
    description: 'Dispute resolved in merchant\'s favor. Held funds of BGN 80.00 released back to merchant',
    timestamp: '15 Jul 2025 09:45',
    requestId: 'req_disp_stu901vwx234',
    status: 'succeeded'
  },
  {
    id: 'evt_disp_008',
    eventType: 'dispute.lost',
    description: 'Dispute resolved in cardholder\'s favor. BGN 80.00 permanently deducted from merchant balance',
    timestamp: '18 Jul 2025 11:30',
    requestId: 'req_disp_vwx234yza567',
    status: 'failed'
  },
  {
    id: 'evt_disp_009',
    eventType: 'dispute.evidence_deadline_approaching',
    description: 'Evidence submission deadline in 48 hours. Reminder sent to merchant admin',
    timestamp: '24 Jun 2025 10:00',
    requestId: 'req_disp_yza567bcd890',
    status: 'pending'
  },
  {
    id: 'evt_disp_010',
    eventType: 'dispute.second_chargeback',
    description: 'Second chargeback (pre-arbitration) filed by cardholder. Additional evidence required',
    timestamp: '20 Jul 2025 08:00',
    requestId: 'req_disp_bcd890efg123',
    status: 'pending'
  },
  {
    id: 'evt_disp_011',
    eventType: 'dispute.accepted',
    description: 'Merchant accepted dispute without challenge. BGN 50.00 refunded to cardholder',
    timestamp: '21 Jul 2025 15:22',
    requestId: 'req_disp_efg123hij456',
    status: 'succeeded'
  }
];

// ========================================
// Activity Events
// ========================================

export const DISPUTE_ACTIVITY_EVENTS: ActivityEvent[] = [
  {
    id: 'activity-dispute-received',
    type: 'dispute_received',
    title: 'Dispute received',
    description: 'Cardholder filed a dispute for BGN 80.00. Reason: Fraudulent transaction.',
    timestamp: '2025-06-19T10:30:00Z',
    timestampDisplay: '19 Jun 2025 10:30',
    severity: 'warning',
    requiresAction: true,
    actionLabel: 'Submit evidence',
    actionCompleted: false,
    icon: 'zap',
    statusCard: {
      title: 'Action Required',
      timestampDisplay: '19 Jun 2025 10:30',
      description: 'Submit supporting evidence by 26 Jun 2025 23:59 UTC to defend the charge.',
    },
    winProbability: 65,
    additionalActions: [
      { label: 'Concede dispute', variant: 'outline' },
      { label: '', variant: 'more' },
    ],
    subEvents: [
      { id: 'sub-funds-held', title: 'Funds held from balance', timestampDisplay: '19 Jun 2025 10:36' },
      { id: 'sub-notification', title: 'Merchant notified via email', timestampDisplay: '19 Jun 2025 10:37' },
    ],
  },
  {
    id: 'activity-evidence-submitted',
    type: 'evidence_submitted',
    title: 'Evidence submitted',
    description: 'Shipping receipt, delivery confirmation, and customer email submitted as evidence.',
    timestamp: '2025-06-22T14:15:00Z',
    timestampDisplay: '22 Jun 2025 14:15',
    severity: 'info',
    requiresAction: false,
    icon: 'file-text',
    details: [
      { action: 'Shipping receipt uploaded', timestamp: '22 Jun 2025 14:12' },
      { action: 'Delivery confirmation uploaded', timestamp: '22 Jun 2025 14:13' },
      { action: 'Customer communication attached', timestamp: '22 Jun 2025 14:14' },
      { action: 'Evidence package submitted to network', timestamp: '22 Jun 2025 14:15' },
    ],
  },
  {
    id: 'activity-original-payment',
    type: 'payment_created',
    title: 'Original payment',
    description: 'Payment of BGN 80.00 via Mastercard ending 0036.',
    timestamp: '2025-06-19T10:00:00Z',
    timestampDisplay: '19 Jun 2025 10:00',
    severity: 'info',
    requiresAction: false,
    icon: 'credit-card',
    details: [
      { action: 'Payment authorized', timestamp: '19 Jun 2025 10:00' },
      { action: '3DS authentication completed', timestamp: '19 Jun 2025 10:01' },
      { action: 'Payment captured', timestamp: '19 Jun 2025 10:02' },
    ],
  },
];

// ========================================
// Receipts
// ========================================

export const DISPUTE_RECEIPTS: Receipt[] = [
  {
    id: 'receipt-dispute-001',
    name: 'Dispute notification',
    timestamp: '19 Jun 2025 10:35'
  },
  {
    id: 'receipt-dispute-002',
    name: 'Evidence submission confirmation',
    timestamp: '22 Jun 2025 14:15'
  },
  {
    id: 'receipt-dispute-003',
    name: 'Original payment receipt',
    timestamp: '19 Jun 2025 10:02'
  },
  {
    id: 'receipt-dispute-004',
    name: 'Dispute resolution notice',
    timestamp: '15 Jul 2025 09:45'
  }
];
